/**
 * AuditTrailPage.tsx
 *
 * Full company-wide audit trail — Pro+ plan feature.
 *
 * Route:  /audit
 *
 * Features:
 *   • Loads up to 500 events from GET /api/expiry/history
 *   • Filter by action type (chip bar)
 *   • Search by item name or description
 *   • Date range picker (Today / 7d / 30d / 90d / All)
 *   • Export CSV
 *   • Plan gate: free-plan users see an upgrade wall instead of data
 *   • Clicking a row opens the item in ItemModal (if item still exists)
 */

import React, { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  format,
  formatDistanceToNow,
  parseISO,
  subDays,
  isAfter,
} from "date-fns";
import { useAllHistory, useImportCsv, type HistoryEvent } from "../hooks/useExpiryItems";
import { EVENT_CONFIG } from "../components/HistoryTimeline";
import { usePlan } from "../hooks/usePlan";
import Header from "../components/Header";
import ImportModal from "../components/ImportModal";
import SettingsModal from "../components/SettingsModal";
// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
type ActionFilter = "all" | HistoryEvent["type"];
type DateRange = "today" | "7d" | "30d" | "90d" | "all";

const DATE_RANGE_LABELS: Record<DateRange, string> = {
  today: "Today",
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  "90d": "Last 90 days",
  all: "All time",
};

const ACTION_FILTERS: Array<{ value: ActionFilter; label: string }> = [
  { value: "all", label: "All activity" },
  { value: "created", label: "Created" },
  { value: "edited", label: "Edited" },
  { value: "renewed", label: "Renewed" },
  { value: "snoozed", label: "Snoozed" },
  { value: "reminder_sent", label: "Reminder sent" },
  { value: "acknowledged", label: "Acknowledged" },
  { value: "assigned", label: "Assigned" },
  { value: "archived", label: "Archived" },
  { value: "restored", label: "Restored" },
];

const PAGE_SIZE = 50;

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
function cutoffForRange(range: DateRange): Date | null {
  const now = new Date();
  if (range === "all") return null;
  if (range === "today")
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const days = range === "7d" ? 7 : range === "30d" ? 30 : 90;
  return subDays(now, days);
}

function exportCsv(rows: HistoryEvent[]): void {
  const headers = ["Date", "Item", "Category", "Action", "Description", "User"];
  const lines = rows.map((r) => [
    format(parseISO(r.date), "yyyy-MM-dd HH:mm"),
    `"${(r.item_name || "").replace(/"/g, '""')}"`,
    `"${(r.item_category || "").replace(/"/g, '""')}"`,
    r.type,
    `"${r.description.replace(/"/g, '""')}"`,
    `"${(r.user || "Automated").replace(/"/g, '""')}"`,
  ]);
  const csv = [headers.join(","), ...lines.map((l) => l.join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `audit-trail-${format(new Date(), "yyyy-MM-dd")}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─────────────────────────────────────────────────────────────────────────────
// Plan gate — full-screen upgrade wall
// ─────────────────────────────────────────────────────────────────────────────
const UpgradeWall: React.FC = () => (
  <div className="flex-1 flex items-center justify-center p-8">
    <div className="max-w-md w-full text-center">
      {/* Lock icon */}
      <div className="h-16 w-16 rounded-2xl bg-[#F3F4F6] flex items-center justify-center mx-auto mb-5">
        <svg
          className="h-8 w-8 text-[#9CA3AF]"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
          />
        </svg>
      </div>

      <h2 className="text-xl font-bold text-[#111827] mb-2">Audit Trail</h2>
      <p className="text-[#6B7280] text-sm leading-relaxed mb-6">
        The full audit trail shows every action taken across all documents — who
        created, edited, renewed, or acknowledged what, and when. Available on
        the <strong>Pro plan</strong> and above.
      </p>

      {/* Feature highlights */}
      <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl p-4 mb-6 text-left space-y-2">
        {[
          "Full company-wide activity log",
          "Filter by action type, date range, and user",
          "Search across all document events",
          "Export to CSV for compliance",
        ].map((f) => (
          <div
            key={f}
            className="flex items-center gap-2.5 text-sm text-[#374151]"
          >
            <svg
              className="h-4 w-4 text-[#2563EB] flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            {f}
          </div>
        ))}
      </div>

      <a
        href="/pricing"
        className="inline-flex items-center gap-2 px-6 py-3 bg-[#2563EB] hover:bg-[#1D4ED8]
                   text-white text-sm font-semibold rounded-xl transition-colors shadow-sm"
      >
        Upgrade to Pro
        <svg
          className="h-4 w-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17 8l4 4m0 0l-4 4m4-4H3"
          />
        </svg>
      </a>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────
const AuditTrailPage: React.FC = () => {
  const navigate = useNavigate();
  const { canUse, plan } = usePlan();
  const hasAccess = canUse("auditTrail");

  // Only fetch when user actually has access
  const {
    data: allEvents = [],
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useAllHistory(hasAccess ? { limit: 500 } : undefined);

  // ── Settings & CSV Import ─────────────────────────────────────────────────
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const importCsvMutation = useImportCsv();

  const handleImport = useCallback(
    async (csvText: string) => {
      await importCsvMutation.mutateAsync(csvText);
      refetch();
    },
    [importCsvMutation, refetch]
  );

  // ── Filters ──────────────────────────────────────────────────────────────
  const [actionFilter, setActionFilter] = useState<ActionFilter>("all");
  const [dateRange, setDateRange] = useState<DateRange>("30d");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  // ── Filtered & paginated events ───────────────────────────────────────────
  const filtered = useMemo(() => {
    const cutoff = cutoffForRange(dateRange);
    const q = search.toLowerCase().trim();

    return allEvents.filter((e) => {
      if (actionFilter !== "all" && e.type !== actionFilter) return false;
      if (cutoff && !isAfter(parseISO(e.date), cutoff)) return false;
      if (q) {
        const haystack = [e.item_name, e.description, e.user, e.item_category]
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [allEvents, actionFilter, dateRange, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const visiblePages = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const resetPage = useCallback(() => setPage(1), []);

  // ── Stats ─────────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const e of allEvents) counts[e.type] = (counts[e.type] || 0) + 1;
    return counts;
  }, [allEvents]);

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      {/* ── Shared app header (hamburger + logo + actions) ────────────────── */}
      <Header
        onImport={() => setIsImportOpen(true)}
        onSettings={() => setIsSettingsOpen(true)}
        onOpenPricing={() => navigate("/pricing")}
      />

      {/* ── Page sub-header ──────────────────────────────────────────────── */}
      <div className="bg-white border-b border-[#E5E7EB] px-6 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/items")}
              className="p-1.5 rounded-lg text-[#6B7280] hover:text-[#111827] hover:bg-[#F3F4F6] transition-colors"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-base font-bold text-[#111827]">
                  Audit Trail
                </h1>
                {/* {hasAccess && (
                  <span
                    className="text-xs bg-[#EFF6FF] text-[#2563EB] border border-[#BFDBFE]
                                   px-2 py-0.5 rounded-full font-medium"
                  >
                    {plan.charAt(0).toUpperCase() + plan.slice(1)}
                  </span>
                )} */}
              </div>
              <p className="text-xs text-[#9CA3AF]">
                Full company-wide activity log
              </p>
            </div>
          </div>

          {hasAccess && (
            <div className="flex items-center gap-2">
              {/* Refresh */}
              <button
                onClick={() => refetch()}
                disabled={isFetching}
                className="p-2 rounded-lg text-[#6B7280] hover:text-[#111827] hover:bg-[#F3F4F6]
                           transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title={isFetching ? "Refreshing…" : "Refresh"}
              >
                <svg
                  className={`w-4 h-4 transition-transform ${isFetching ? "animate-spin" : "hover:rotate-180 duration-300"}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
              </button>

              {/* Export CSV */}
              <button
                onClick={() => exportCsv(filtered)}
                disabled={filtered.length === 0}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium
                           border border-[#D1D5DB] rounded-lg text-[#374151]
                           hover:bg-[#F9FAFB] disabled:opacity-40 transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  />
                </svg>
                Export CSV
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Modals ───────────────────────────────────────────────────────── */}
      {isSettingsOpen && (
        <SettingsModal onClose={() => setIsSettingsOpen(false)} />
      )}
      {isImportOpen && (
        <ImportModal
          onClose={() => setIsImportOpen(false)}
          onImport={handleImport}
        />
      )}

      {/* ── Plan gate ────────────────────────────────────────────────────── */}
      {!hasAccess ? (
        <UpgradeWall />
      ) : (
        <div className="max-w-7xl w-full mx-auto px-6 py-6 space-y-5">
          {/* ── Summary stat chips ───────────────────────────────────────── */}
          {!isLoading && allEvents.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {(
                [
                  "created",
                  "edited",
                  "renewed",
                  "reminder_sent",
                  "acknowledged",
                ] as const
              )
                .filter((t) => (stats[t] ?? 0) > 0)
                .map((t) => {
                  const cfg = EVENT_CONFIG[t];
                  return (
                    <div
                      key={t}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full
                                  text-xs font-semibold border ${cfg.bg} ${cfg.color}`}
                      style={{ borderColor: "transparent" }}
                    >
                      {cfg.icon}
                      {stats[t]} {cfg.label.toLowerCase()}
                    </div>
                  );
                })}
            </div>
          )}

          {/* ── Filters row ──────────────────────────────────────────────── */}
          <div className="bg-white border border-[#E5E7EB] rounded-xl p-4 space-y-3">
            {/* Search + Date range */}
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Search */}
              <div className="relative flex-1">
                <svg
                  className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <input
                  type="text"
                  placeholder="Search by document name, action, or user…"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    resetPage();
                  }}
                  className="w-full pl-9 pr-4 py-2 text-sm border border-[#D1D5DB] rounded-lg
                             focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent"
                />
                {search && (
                  <button
                    onClick={() => {
                      setSearch("");
                      resetPage();
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#374151]"
                  >
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>

              {/* Date range */}
              <select
                value={dateRange}
                onChange={(e) => {
                  setDateRange(e.target.value as DateRange);
                  resetPage();
                }}
                className="px-3 py-2 text-sm border border-[#D1D5DB] rounded-lg bg-white
                           focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
              >
                {(Object.keys(DATE_RANGE_LABELS) as DateRange[]).map((r) => (
                  <option key={r} value={r}>
                    {DATE_RANGE_LABELS[r]}
                  </option>
                ))}
              </select>
            </div>

            {/* Action type chips */}
            <div className="flex flex-wrap gap-1.5">
              {ACTION_FILTERS.map(({ value, label }) => {
                const isActive = actionFilter === value;
                const cfg =
                  value !== "all"
                    ? EVENT_CONFIG[value as HistoryEvent["type"]]
                    : null;
                return (
                  <button
                    key={value}
                    onClick={() => {
                      setActionFilter(value);
                      resetPage();
                    }}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full
                                text-xs font-medium transition-all border ${
                                  isActive
                                    ? cfg
                                      ? `${cfg.bg} ${cfg.color} border-current`
                                      : "bg-[#111827] text-white border-[#111827]"
                                    : "bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#D1D5DB] hover:text-[#374151]"
                                }`}
                  >
                    {cfg && isActive && <span>{cfg.icon}</span>}
                    {label}
                    {value !== "all" && (stats[value] ?? 0) > 0 && (
                      <span
                        className={`ml-0.5 ${isActive ? "opacity-70" : "text-[#9CA3AF]"}`}
                      >
                        {stats[value]}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Table ────────────────────────────────────────────────────── */}
          <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
            {/* Table header */}
            <div className="px-5 py-3 border-b border-[#F3F4F6] flex items-center justify-between">
              <p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide">
                {filtered.length === 0
                  ? "No events"
                  : `${filtered.length} event${filtered.length !== 1 ? "s" : ""}${
                      filtered.length !== allEvents.length
                        ? ` (filtered from ${allEvents.length})`
                        : ""
                    }`}
              </p>
              {totalPages > 1 && (
                <p className="text-xs text-[#9CA3AF]">
                  Page {page} of {totalPages}
                </p>
              )}
            </div>

            {/* Loading */}
            {isLoading && (
              <div className="flex items-center justify-center py-16">
                <div className="h-8 w-8 rounded-full border-2 border-[#2563EB] border-t-transparent animate-spin" />
              </div>
            )}

            {/* Error */}
            {isError && !isLoading && (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <p className="text-sm font-medium text-[#374151] mb-1">
                  Failed to load audit trail
                </p>
                <button
                  onClick={() => refetch()}
                  className="text-xs text-[#2563EB] underline"
                >
                  Try again
                </button>
              </div>
            )}

            {/* Empty */}
            {!isLoading && !isError && filtered.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="h-12 w-12 rounded-full bg-[#F3F4F6] flex items-center justify-center mb-3">
                  <svg
                    className="h-6 w-6 text-[#9CA3AF]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    />
                  </svg>
                </div>
                <p className="text-sm font-medium text-[#374151]">
                  No matching events
                </p>
                <p className="text-xs text-[#9CA3AF] mt-1">
                  {allEvents.length === 0
                    ? "Activity will appear here as your team works."
                    : "Try adjusting the filters or date range."}
                </p>
                {(actionFilter !== "all" || dateRange !== "all" || search) && (
                  <button
                    onClick={() => {
                      setActionFilter("all");
                      setDateRange("30d");
                      setSearch("");
                      resetPage();
                    }}
                    className="mt-3 text-xs text-[#2563EB] underline"
                  >
                    Clear all filters
                  </button>
                )}
              </div>
            )}

            {/* Rows */}
            {!isLoading && !isError && visiblePages.length > 0 && (
              <>
                {/* Desktop: full table */}
                <div className="hidden sm:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[#F3F4F6]">
                        {[
                          "Action",
                          "Document",
                          "Category",
                          "Description",
                          "User",
                          "When",
                        ].map((h) => (
                          <th
                            key={h}
                            className="px-4 py-2.5 text-left text-xs font-semibold text-[#9CA3AF]
                                       uppercase tracking-wide whitespace-nowrap"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#F9FAFB]">
                      {visiblePages.map((event) => {
                        const cfg =
                          EVENT_CONFIG[event.type] ?? EVENT_CONFIG.edited;
                        return (
                          <tr
                            key={event.id}
                            onClick={() =>
                              event.item_id &&
                              navigate(`/items?open=${event.item_id}`)
                            }
                            className={`group hover:bg-[#F9FAFB] transition-colors ${
                              event.item_id ? "cursor-pointer" : ""
                            }`}
                          >
                            {/* Action badge */}
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span
                                className={`inline-flex items-center gap-1.5 px-2.5 py-1
                                              rounded-full text-xs font-semibold ${cfg.bg} ${cfg.color}`}
                              >
                                {cfg.icon}
                                {cfg.label}
                              </span>
                            </td>

                            {/* Document name */}
                            <td className="px-4 py-3 max-w-[180px]">
                              <p
                                className={`text-sm font-medium truncate ${
                                  event.item_id
                                    ? "text-[#2563EB] group-hover:underline"
                                    : "text-[#374151]"
                                }`}
                              >
                                {event.item_name || (
                                  <span className="text-[#9CA3AF] italic">
                                    Deleted
                                  </span>
                                )}
                              </p>
                            </td>

                            {/* Category */}
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className="text-xs text-[#6B7280] bg-[#F3F4F6] px-2 py-0.5 rounded-full">
                                {event.item_category || "—"}
                              </span>
                            </td>

                            {/* Description */}
                            <td className="px-4 py-3 text-sm text-[#374151] max-w-[240px]">
                              <p className="truncate">{event.description}</p>
                              {/* Renewal date diff inline */}
                              {event.type === "renewed" &&
                                event.meta?.previousDate &&
                                event.meta?.newDate && (
                                  <p className="text-xs mt-0.5">
                                    <span className="line-through text-[#EF4444]">
                                      {event.meta.previousDate}
                                    </span>
                                    {" → "}
                                    <span className="text-[#15803D] font-medium">
                                      {event.meta.newDate}
                                    </span>
                                  </p>
                                )}
                              {/* Field edit diff inline */}
                              {event.type === "edited" && event.meta?.field && (
                                <p className="text-xs mt-0.5 text-[#6B7280]">
                                  <span className="font-medium capitalize">
                                    {event.meta.field}:{" "}
                                  </span>
                                  <span className="line-through text-[#EF4444]">
                                    {String(event.meta.previousValue ?? "")}
                                  </span>
                                  <span className="mx-1 text-[#9CA3AF]">→</span>
                                  <span className="text-[#15803D]">
                                    {String(event.meta.newValue ?? "")}
                                  </span>
                                </p>
                              )}
                            </td>

                            {/* User */}
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <div
                                  className="h-6 w-6 rounded-full bg-[#EFF6FF] flex items-center
                                                justify-center text-[#2563EB] text-xs font-bold flex-shrink-0"
                                >
                                  {(event.user || "A")[0].toUpperCase()}
                                </div>
                                <span className="text-xs text-[#374151] truncate max-w-[100px]">
                                  {event.user || "Automated"}
                                </span>
                              </div>
                            </td>

                            {/* When */}
                            <td className="px-4 py-3 whitespace-nowrap text-xs text-[#9CA3AF]">
                              <p>
                                {formatDistanceToNow(parseISO(event.date), {
                                  addSuffix: true,
                                })}
                              </p>
                              <p className="mt-0.5">
                                {format(
                                  parseISO(event.date),
                                  "MMM d, yyyy · HH:mm"
                                )}
                              </p>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Mobile: card list */}
                <div className="sm:hidden divide-y divide-[#F3F4F6]">
                  {visiblePages.map((event) => {
                    const cfg = EVENT_CONFIG[event.type] ?? EVENT_CONFIG.edited;
                    return (
                      <div
                        key={event.id}
                        onClick={() =>
                          event.item_id &&
                          navigate(`/items?open=${event.item_id}`)
                        }
                        className={`px-4 py-4 ${event.item_id ? "cursor-pointer active:bg-[#F9FAFB]" : ""}`}
                      >
                        <div className="flex items-start gap-3">
                          {/* Icon */}
                          <div
                            className={`h-8 w-8 rounded-full flex items-center justify-center
                                          flex-shrink-0 mt-0.5 ${cfg.bg} ${cfg.color}`}
                          >
                            {cfg.icon}
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                              <span
                                className={`text-xs font-semibold ${cfg.color}`}
                              >
                                {cfg.label}
                              </span>
                              <span className="text-xs text-[#9CA3AF]">
                                {formatDistanceToNow(parseISO(event.date), {
                                  addSuffix: true,
                                })}
                              </span>
                            </div>
                            {event.item_name && (
                              <p className="text-sm font-medium text-[#111827] truncate mt-0.5">
                                {event.item_name}
                              </p>
                            )}
                            <p className="text-xs text-[#6B7280] mt-0.5 line-clamp-2">
                              {event.description}
                            </p>
                            <p className="text-xs text-[#9CA3AF] mt-1">
                              by {event.user || "Automated"}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* ── Pagination ──────────────────────────────────────────── */}
            {totalPages > 1 && (
              <div className="px-5 py-3 border-t border-[#F3F4F6] flex items-center justify-between gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-[#374151]
                             border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB]
                             disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <svg
                    className="h-3.5 w-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                  Previous
                </button>

                {/* Page numbers */}
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const p =
                      page <= 3
                        ? i + 1
                        : page >= totalPages - 2
                          ? totalPages - 4 + i
                          : page - 2 + i;
                    if (p < 1 || p > totalPages) return null;
                    return (
                      <button
                        key={p}
                        onClick={() => setPage(p)}
                        className={`h-7 w-7 text-xs rounded-lg transition-colors ${
                          p === page
                            ? "bg-[#2563EB] text-white font-semibold"
                            : "text-[#6B7280] hover:bg-[#F3F4F6]"
                        }`}
                      >
                        {p}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-[#374151]
                             border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB]
                             disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                  <svg
                    className="h-3.5 w-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditTrailPage;
